import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Heart, Loader2 } from "lucide-react";
import { DONATION_PRESETS, PAYMENT_HANDLES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "card", label: "Card" },
  { id: "zelle", label: "Zelle" },
  { id: "cashapp", label: "Cash App" },
];

export const Donate = () => {
  const [tab, setTab] = useState("card");
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const value = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    if (!value || value < 1) {
      toast.error("Please enter a donation amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        amount: value,
        origin_url: window.location.origin,
        donor_name: name || null,
        donor_email: email || null,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("No checkout URL");
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${text}`);
  };

  return (
    <section id="donate" data-testid="donate" className="relative bg-[#0a0a0a] py-24 md:py-36 border-y border-[#1a1a1a]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="label mb-5">Fuel The Mission</p>
          <h2 className="font-display uppercase text-white leading-[0.95] text-4xl sm:text-6xl">
            Give a <span className="text-green glow-green">meal</span>, give a morning
          </h2>
          <p className="font-sub text-lg text-zinc-400 mt-6 font-light max-w-md">
            Every dollar becomes food, water, juice, clothes and shoes handed directly to
            someone who needs it. 100% goes to the streets.
          </p>
          <ul className="mt-8 space-y-3 font-sub text-zinc-300">
            <li className="flex items-center gap-3"><span className="text-green">$10</span> — 5 hot meals</li>
            <li className="flex items-center gap-3"><span className="text-green">$25</span> — a full care kit</li>
            <li className="flex items-center gap-3"><span className="text-green">$100</span> — an entire outreach run</li>
          </ul>
        </motion.div>

        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="bg-[#050505] border border-[#1a1a1a] p-6 md:p-10">
            <div className="flex gap-2 mb-8 border border-[#1a1a1a] p-1 rounded-full w-fit">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`donate-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`font-sub text-sm px-5 py-2 rounded-full transition-colors duration-300 ${
                    tab === t.id ? "bg-green text-black font-semibold" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "card" && (
              <div data-testid="donate-card-panel">
                <div className="grid grid-cols-4 gap-3 mb-5">
                  {DONATION_PRESETS.map((p) => (
                    <button
                      key={p}
                      data-testid={`donate-preset-${p}`}
                      onClick={() => { setAmount(p); setCustom(""); }}
                      className={`font-display text-2xl py-4 border transition-colors duration-300 ${
                        !custom && amount === p
                          ? "border-green text-green box-glow"
                          : "border-[#1a1a1a] text-white hover:border-white/40"
                      }`}
                    >
                      ${p}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="donate-custom-amount"
                  type="number"
                  min="1"
                  placeholder="Custom amount (USD)"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="field mb-4"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <input data-testid="donate-name" className="field" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
                  <input data-testid="donate-email" type="email" className="field" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button
                  data-testid="donate-submit"
                  onClick={donate}
                  disabled={loading}
                  className="btn-fill w-full border border-green text-green font-sub font-semibold text-lg py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : <Heart size={18} />}
                  {loading ? "Redirecting…" : `Donate $${value || 0} securely`}
                </button>
                <p className="text-center text-xs text-zinc-600 mt-3 font-sub">Secure checkout via Stripe · test card 4242 4242 4242 4242</p>
              </div>
            )}

            {tab === "zelle" && (
              <HandlePanel
                testid="donate-zelle-panel"
                title="Send with Zelle"
                handle={PAYMENT_HANDLES.zelle}
                note="Open your bank's Zelle, send to the address below, and add a note 'Mask Giver'."
                onCopy={copy}
              />
            )}

            {tab === "cashapp" && (
              <HandlePanel
                testid="donate-cashapp-panel"
                title="Send with Cash App"
                handle={PAYMENT_HANDLES.cashapp}
                note="Open Cash App, tap Pay, and send to our $Cashtag below."
                onCopy={copy}
              />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const HandlePanel = ({ testid, title, handle, note, onCopy }) => (
  <div data-testid={testid} className="py-4">
    <h3 className="font-sub text-2xl text-white font-semibold mb-2">{title}</h3>
    <p className="font-sub text-zinc-400 mb-8 max-w-md">{note}</p>
    <div className="flex items-center justify-between gap-4 border border-[#1a1a1a] bg-[#0a0a0a] px-6 py-5">
      <span className="font-display text-2xl md:text-3xl text-green break-all">{handle}</span>
      <button
        data-testid={`${testid}-copy`}
        onClick={() => onCopy(handle)}
        className="shrink-0 flex items-center gap-2 border border-green text-green btn-fill px-4 py-2 rounded-full font-sub text-sm"
      >
        <Copy size={16} /> Copy
      </button>
    </div>
  </div>
);
